/* eslint-disable react-hooks/exhaustive-deps */

import { Box } from '@zoopla-eng/zds-components/box';
import {
    Breadcrumbs,
    BreadcrumbsProps
} from '@zoopla-eng/zds-components/breadcrumbs';
import {
    PageChangeEvent,
    Pagination
} from '@zoopla-eng/zds-components/pagination';
import { Skeleton } from '@zoopla-eng/zds-components/skeleton';
import { AuthModal } from '@zoopla/authentication/modal';
import { useAuthModal } from '@zoopla/authentication/use-auth-modal';
import { useBreakpointListener } from '@zoopla/dom-util';
import { Head } from '@zoopla/layout/head';
import {
    Links,
    ProductTrackingObject,
    SearchContext,
    SearchResultMeta,
    SeoBlurb,
    ServerData
} from '@zoopla/listing-search-context';
import {
    buildQueryParamsString,
    getAllFilterStateForTracking,
    navigateToResultsPage
} from '@zoopla/listing-search-utils';
import { internalPromoIds } from '@zoopla/listing-search/featured/properties';
import {
    FeaturedPropertyList,
    getProductImpression
} from '@zoopla/listing-search/featured/property-list';
import { Listing, Listings } from '@zoopla/listing-search/listings';
import { segmentSearchEvent } from '@zoopla/listing-search/segment';
import { updatePathPageNumber } from '@zoopla/listing-search/url';
import { useSaveListing } from '@zoopla/listing-search/use-save-listing';
import { SeoSrpCopy } from '@zoopla/seo/srp-copy';
import { EventNames, trackPage, triggerTracking } from '@zoopla/tracking';
import { useIsLoggedIn } from '@zoopla/user';
import dynamic from 'next/dynamic';
import { useRouter } from 'next/router';
import {
    ComponentProps,
    FC,
    MouseEvent,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useState
} from 'react';
import { DiscoverMore } from './DiscoverMore';
import { FeaturedListingsSkeleton } from './FeaturedListingsSkeleton/FeaturedListingsSkeleton';
import { HeaderSkeleton } from './HeaderSkeleton';
import { InViewMount } from './InViewMount/InViewMount';
import { ListingsSkeleton } from './ListingsSkeleton';
import {
    searchResultsLayoutGroupStyle,
    skeletonContainerStyle
} from './Results.styles.css';
import { getIdentifier, getLocation } from './searchQueryUtil';
import { SearchResultsHeader } from './SearchResultsHeader';
import { shouldIndexPath } from './Seo/noFollow';
import { getJSONLD } from './Seo/schemaMarkup';
import { SituationLoggedOutModal } from './SituationModal/SituationLoggedOutModal';

const removeEverythingAfterHash = (path?: string) => {
    if (!path) {
        return '';
    }

    return path.replace(/#.*/, '');
};

const EmailAllAgentsWrapper = dynamic<
    ComponentProps<
        typeof import('@zoopla/listing-search/email-agents').EmailAllAgentsWrapper
    >
>(
    () =>
        import('@zoopla/listing-search/email-agents').then(
            (module) => module.EmailAllAgentsWrapper
        ),
    {
        ssr: false
    }
);

type PromoIds = (typeof internalPromoIds)[number];

type InternalPromoId = (typeof internalPromoIds)[number];

type Product =
    | {
          listingId: string;
          featuredType?: string | null;
          title?: string | null;
          price?: string | null;
          alternativeRentFrequencyLabel?: string | null;
          address?: string | null;
          branch?: { logoUrl?: string | null; name?: string | null } | null;
          listingUris?: {
              detail?: string | null;
          } | null;
          features?:
              | ({
                    iconId?: string | null;
                    content?: number | null;
                } | null)[]
              | null;
          image?: {
              src?: string | null;
              responsiveImgList?:
                  | ({ src?: string | null; width?: number | null } | null)[]
                  | null;
          } | null;
      }
    | InternalPromoId;

export type SearchResultProps = {
    regularListings: Listing[];
    extendedListings: Listing[];
    readonly featuredProperties: (Product | PromoIds)[];
    canonicalPath: string;
    searchResults?: SearchResultMeta;
    discoverMore?: ServerData['discoverMore'];
    breadcrumbs?: ServerData['breadcrumbs'];
    loading: boolean;
    transactionType: SeoBlurb['transactionType'];
    error?: { message: string };
    links: Links;
};

const SearchResults: FC<React.PropsWithChildren<SearchResultProps>> = ({
    regularListings,
    extendedListings,
    featuredProperties,
    canonicalPath,
    searchResults,
    breadcrumbs,
    discoverMore,
    loading: isLoading,
    transactionType,
    links,
    error
}) => {
    const router = useRouter();
    const { query, asPath, route, pathname } = router;
    const path = removeEverythingAfterHash(asPath);
    const filtersTracking = useMemo(
        () => getAllFilterStateForTracking(query, pathname),
        [query, pathname]
    );
    const { state, dispatch } = useContext(SearchContext);
    const searchQuery = useMemo(() => {
        const location = getLocation(query);
        const identifier = getIdentifier(query);
        return {
            location: location ?? state.locationByIdentifier ?? '',
            identifier
        };
    }, [query, state.locationByIdentifier]);

    const {
        searchType,
        currentSearchSource,
        favourites,
        actionAfterAuth,
        seoAccordions
    } = state;

    const isLoggedIn = useIsLoggedIn();

    const totalPages = state.pageNumberMax;
    const pageNumber = state.pageNumber;

    const isBelowShowSidebarBreakpoint = useBreakpointListener(1140, 'LT');

    const [internalPromoParam, setInternalPromoParam] = useState('');

    const [isAuthModalShowing, openAuthModal, closeAuthModal] = useAuthModal();
    const [authHeader, setAuthHeader] = useState<string | undefined>(undefined);

    const authenticateSaveToggle = useSaveListing(
        dispatch,
        favourites.listings,
        isLoggedIn,
        setAuthHeader,
        openAuthModal,
        'search results'
    );

    useEffect(() => {
        const promoParam = buildQueryParamsString(query, 1);

        setInternalPromoParam(promoParam);
    }, [query['q']]);

    // All tracking handled in one effect
    useEffect(() => {
        const controller = new AbortController();
        if (!searchResults?.analyticsTaxonomy?.page) {
            return;
        }
        const { analyticsTaxonomy: analyticsTaxonomyFromResponse } =
            searchResults;

        const analyticsTaxonomy = {
            ...analyticsTaxonomyFromResponse,
            location: analyticsTaxonomyFromResponse.areaName
        };

        let featuredImpressions: ProductTrackingObject[] = [];

        if (!isBelowShowSidebarBreakpoint && featuredProperties.length !== 0) {
            featuredImpressions = featuredProperties
                .map((featuredProperty, index) =>
                    getProductImpression(featuredProperty, index + 1)
                )
                // Only interested in unfilled featured entries (i.e. featured product Ad entries) as
                // featured listings are already present in the analyticsEcommerce array
                .filter((impression) => impression.id === 'Unfilled');
        }

        const analyticsEcommerce = {
            currencyCode: searchResults.analyticsEcommerce.currencyCode,
            impressions: [
                ...(searchResults.analyticsEcommerce.impressions ?? []),
                ...featuredImpressions
            ]
        };

        trackPage(
            analyticsTaxonomy.page,
            analyticsTaxonomy,
            analyticsEcommerce,
            controller.signal
        );
        segmentSearchEvent({
            eventName: EventNames.SEARCH,
            analyticsTaxonomy,
            currentSearchSource,
            searchCategory: searchResults.seoBlurb.category,
            filters: filtersTracking
        });
        return () => {
            controller.abort();
        };
    }, [searchResults]);

    /**
   NOTE:
   This param is for internal ads in featured property list (Map and New Home link)
   Both links start a new search using search param in results page, e.g. "q=london&radius=0"
   */
    const currentPage =
        typeof query['pn'] === 'string' ? parseInt(query['pn']) : 1;

    const handleChangePage = async ({ source, page }: PageChangeEvent) => {
        if (typeof window !== 'undefined') {
            window.scrollTo(0, 0);
        }

        if (source === 'next') {
            triggerTracking({
                event: 'GAEvent',
                eventCategory: 'search results',
                eventAction: 'click',
                eventLabel: `pagination_next`
            });
        } else if (source === 'previous') {
            triggerTracking({
                event: 'GAEvent',
                eventCategory: 'search results',
                eventAction: 'click',
                eventLabel: `pagination_back`
            });
        } else {
            triggerTracking({
                event: 'GAEvent',
                eventCategory: 'search results',
                eventAction: 'click',
                eventLabel: `page_${page}`
            });
        }
        router.push(pageNumberToHref(page));
    };

    const pageNumberToHref = (pageNumber: number): string => {
        const currentPath = router.asPath.replace('&view_type=list', '');

        if (currentPage === pageNumber) return router.asPath;

        return updatePathPageNumber(currentPath, pageNumber);
    };

    const onResetFilters = useCallback(
        (evt: MouseEvent<HTMLButtonElement>) => {
            evt.preventDefault();

            // just preserve search type and query
            navigateToResultsPage(
                { q: query['q'] ?? '', section: searchType },
                router.push
            );
        },
        [query, searchType]
    );

    const authModal = (
        <AuthModal
            isShowing={isAuthModalShowing}
            close={(hasAuthenticated) => {
                closeAuthModal();

                if (hasAuthenticated) {
                    actionAfterAuth?.();
                }
            }}
            modalAuthHeading={authHeader}
            trackingData={{
                label: 'save_listing',
                segmentData: {
                    page:
                        searchResults?.analyticsTaxonomy?.page ||
                        '/for-sale/results/',
                    section:
                        searchResults?.analyticsTaxonomy?.section || 'for-sale'
                }
            }}
        />
    );

    if (isLoading) {
        return (
            <Skeleton.Root loadingText="fetching results">
                <div className={searchResultsLayoutGroupStyle}>
                    <div className={skeletonContainerStyle}>
                        <HeaderSkeleton />
                        <Box display="none" bpL100={{ display: 'flex' }}>
                            <FeaturedListingsSkeleton />
                        </Box>
                        <ListingsSkeleton />
                    </div>
                </div>
            </Skeleton.Root>
        );
    }

    // TODO: ZDSU how does tracking work?
    const zdsBreadcrumbs = breadcrumbs?.reduce<BreadcrumbsProps['items']>(
        (bcs, bc, indx) => {
            if (!bc.uri || !bc.label) {
                return bcs;
            }

            return [
                ...bcs,
                {
                    id: bc.i18nLabelKey ?? `id-${indx}`,
                    href: bc.uri,
                    text: bc.label,
                    ariaLabel: bc.label
                }
            ];
        },
        []
    );

    return (
        <>
            <Head
                pageTitle={searchResults?.metaInfo?.title || ''}
                pageDescription={searchResults?.metaInfo?.description || ''}
                canonicalPath={canonicalPath}
                renderSocialSharingTags={true}
                index={() =>
                    shouldIndexPath(
                        asPath,
                        query,
                        searchResults?.seoBlurb?.category || '',
                        pageNumber,
                        regularListings.length,
                        extendedListings.length
                    )
                }
                schema={() =>
                    getJSONLD(
                        asPath,
                        searchResults?.metaInfo?.canonicalUri || '',
                        breadcrumbs || []
                    )
                }
            />
            <SearchResultsHeader pageTitle={searchResults?.title} />
            <div className={searchResultsLayoutGroupStyle}>
                <FeaturedPropertyList
                    properties={featuredProperties}
                    sectionType={transactionType}
                    propertyType={seoAccordions[0]?.propertyType}
                    identifier={searchQuery.identifier}
                    searchParam={internalPromoParam}
                    searchIdentifier={
                        searchResults?.analyticsTaxonomy?.searchIdentifier || ''
                    }
                />
                <Listings
                    searchType={searchType}
                    analyticsTaxonomyIdentifier={
                        searchResults?.analyticsTaxonomy?.searchIdentifier || ''
                    }
                    analyticsTaxonomy={searchResults?.analyticsTaxonomy}
                    analyticsEcommerce={searchResults?.analyticsEcommerce}
                    searchCategory={searchResults?.seoBlurb?.category ?? ''}
                    filters={filtersTracking}
                    currentSearchSource={currentSearchSource}
                    error={error}
                    regularListings={regularListings}
                    extendedListings={extendedListings}
                    onResetFilters={onResetFilters}
                    favouritesListings={favourites.listings}
                    authenticateSaveToggle={authenticateSaveToggle}
                    authModal={authModal}
                    pageNumber={pageNumber}
                    route={route}
                />
                {regularListings.length > 0 && (
                    <div data-testid="pagination">
                        <Pagination
                            totalPages={totalPages ?? 0}
                            currentPage={currentPage}
                            pageNumberToURL={() => '#__next'}
                            onPageChange={handleChangePage}
                        />
                    </div>
                )}

                <InViewMount opts={{ rootMargin: '300px 0px 300px 0px' }}>
                    <EmailAllAgentsWrapper
                        path={path}
                        searchType={searchType}
                        location={searchQuery.location}
                        isUserLoggedIn={isLoggedIn}
                        createAlert={links?.createAlert || ''}
                        analyticsTaxonomy={searchResults?.analyticsTaxonomy}
                    />
                </InViewMount>

                {zdsBreadcrumbs && (
                    <Box pt="M100">
                        <Breadcrumbs
                            data-testid="breadcrumbs"
                            items={zdsBreadcrumbs}
                        />
                    </Box>
                )}

                {searchResults?.analyticsTaxonomy?.geoIdentifier &&
                searchType === 'for-sale' ? (
                    <SeoSrpCopy
                        geoIdentifier={
                            searchResults?.analyticsTaxonomy?.geoIdentifier ??
                            ''
                        }
                    />
                ) : null}

                <DiscoverMore
                    findAgentsUri={discoverMore?.findAgentsUri || ''}
                    housePricesUri={discoverMore?.housePricesUri || ''}
                    searchArea={
                        searchResults?.analyticsTaxonomy?.areaName || ''
                    }
                />

                <SituationLoggedOutModal pageType={searchType} />
            </div>
        </>
    );
};

export default SearchResults;
